import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState = {
};

interface SearchParams {
  logDateFrom: string,
  logDateTo: string,
  userNameLike: string,
  menuNameLike: string,
  useType: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  logDateFrom: dayjs().format("YYYYMMDD"),
  logDateTo: dayjs().format("YYYYMMDD"),
  userNameLike: "",
  menuNameLike: "",
  useType: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function SystemUsageLog(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "로그생성시간", field: "logDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "이름", headerClass: "hd-ta-left", field: "userName", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "메뉴", field: "menuName", width: 150, cellStyle: {textAlign: "center"} },
    { headerName: "작업종류", field: "useType", width: 150, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("useType") },
    { headerName: "작업내용", headerClass: "hd-ta-left", field: "useContent", width: 400, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "URL", headerClass: "hd-ta-left", field: "url", width: 400, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/log/system_usage_log.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/log/system_usage_log", searchParams);
  }

  const goPage = useCallback((page: number) => {
      const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/log/system_usage_log", searchParams);
    }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/log/system_usage_log", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/log/system_usage_log.download?mode=search", searchParams, {
        "fileName": "시스템사용로그_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>시스템 사용 로그</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-range">
            <p>조회일자</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.logDateFrom)}
                  onChange={(e) => {doSearch(e, {logDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.logDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.logDateTo)}
                  onChange={(e) => {doSearch(e, {logDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.logDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>이름</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.userNameLike}
              onChange={(e) => {updateSearchParams({ userNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="이름"
            />
          </div>
          <div>
            <p>메뉴</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.menuNameLike}
              onChange={(e) => {updateSearchParams({ menuNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="메뉴"
            />
          </div>
          <div>
            <p>작업종류</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.useType}
                onChange={(e) => {doSearch(e, {useType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.useType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 로그 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 로그 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(SystemUsageLog);
